import { Component, OnInit, Input } from '@angular/core';
import { Helpers } from 'src/app/helpers/helpers';
import { BallotsService } from 'src/app/services/ballots.service';
import { UserService } from 'src/app/services/user.service';
import { AlertController, LoadingController } from '@ionic/angular';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'app-ballot-creator',
  templateUrl: './ballot-creator.component.html',
  styleUrls: ['./ballot-creator.component.scss'],
})
export class BallotCreatorComponent implements OnInit {

  @Input() type: string;

  matrix: string;
  matrixArray: Array<string>;
  players: Array<any> = new Array<any>();

  currentStep: string; // matrix|ballot|player
  currentStepObservable: BehaviorSubject<string> = new BehaviorSubject<string>('');
  currentIndex: number;

  constructor(
    private router: Router,
    private ballotsService: BallotsService,
    private userService: UserService,
    private loadingController: LoadingController,
    private alertController: AlertController
  ) { }

  async ngOnInit() {
    this.goTo('matrix');
    this.currentStepObservable.subscribe((step) => {
      if (!!step === false) { return; }
      if (step === 'player') {
        this.scrollToTop();
      }
    });
  }

  goTo(step: string) {
    this.currentStep = step;
    this.currentStepObservable.next(step);
  }

  scrollToTop() {
    document.getElementById('ballot-creator').parentElement.parentElement.parentElement.parentElement.scrollIntoView();
  }

  setMatrix(matrix: string) {
    this.matrix = matrix;
    this.matrixArray = Helpers.extractBallotMatrixToArray(matrix);
    this.setPlayers(this.matrixArray);
    this.goTo('ballot');
  }

  private setPlayers(matrixArray) {
    this.players = new Array<any>();
    const pos0Value = parseInt(matrixArray[0], 10);
    const pos1Value = parseInt(matrixArray[1], 10);
    const playersCount = (pos0Value === pos1Value) ? (pos0Value * 2) : pos1Value;
    for (let i = 0; i < playersCount; i++) { this.players.push({}); }
  }

  selectPlayer(index) {
    this.currentIndex = index;
    this.goTo('player');
  }

  playerSelected(player) {
    this.players[this.currentIndex] = player;
  }

  async saveBallot(event) {
    const loading = await this.loadingController.create({ message: 'Salvataggio...' });
    await loading.present();

    let response;
    const params = this.getParams();

    // Parameters validation
    const areParamsValid = this.validateParams(this.type, params.matrixData);
    if (areParamsValid === false) {
      await loading.dismiss();
      return false;
    }

    switch (this.type) {
      case 'exchange':
        response = await this.ballotsService.createExchangeBallot(params.toSend);
        break;
      case 'formation':
        response = await this.ballotsService.createFormationBallot(params.toSend);
        break;
      case 'purchase':
        response = await this.ballotsService.createPurchaseBallot(params.toSend);
        break;
    }

    loading.dismiss();

    if (!!response) {
      this.goTo('matrix');
      this.showSaveBallotSuccessMessage();
      this.userService.refreshLoggedUser(response.user);
      this.router.navigate(['/home']);
    }
  }

  getParams() {
    const params = {
      toSend: {
        matrix: null,
        matrix_data: null
      },
      matrixData: null
    };

    let matrixData;
    if (this.type === 'exchange') {
      params.toSend.matrix = this.matrixArray[0];
      matrixData = {
        left: this.players.filter((p, index) => { if (this.isOdd(index) === false) { return p; } }).map((p) => p.id),
        right: this.players.filter((p, index) => { if (this.isOdd(index) === true) { return p; } }).map((p) => p.id)
      };
    } else {
      params.toSend.matrix = `${this.matrixArray[0]}:${this.matrixArray[1]}`;
      matrixData = this.players.map(p => p.id);
    }


    params.matrixData = matrixData;
    params.toSend.matrix_data = JSON.stringify(matrixData);

    return params;
  }

  validateParams(type, matrixData) {
    if (type === 'exchange') {

      const leftPlayers = matrixData.left;
      const rightPlayers = matrixData.right;

      // EXCHANGE: Check for empty players
      const leftEmptyIndex = leftPlayers.findIndex(p => !!p === false);
      const rightEmptyIndex = rightPlayers.findIndex(p => !!p === false);
      if (leftEmptyIndex !== -1 || rightEmptyIndex !== -1) {
        this.showEmptyPlayerErrorAlert();
        return false;
      }

      // EXCHANGE: Check for duplicates
      const duplicatesPlayers = this.findDuplicates([...leftPlayers, ...rightPlayers]);
      if (duplicatesPlayers.length > 0) {
        this.showDuplicatePlayerErrorAlert();
        return false;
      }

    } else {

      // FORMATION & PURCHASE: Check for empty players
      const emptyIndex = matrixData.findIndex(p => !!p === false);
      if (emptyIndex !== -1) {
        this.showEmptyPlayerErrorAlert();
        return false;
      }

      // FORMATION & PURCHASE: Check for duplicates
      const duplicatesPlayers = this.findDuplicates(matrixData);
      if (duplicatesPlayers.length > 0) {
        this.showDuplicatePlayerErrorAlert();
        return false;
      }
    }
    return true;
  }

  isOdd(num: number) {
    if (num === 0) { return false; }
    return num % 2 === 1;
  }

  findDuplicates(data) {
    const result = [];
    data.forEach((element, index) => {
      // Find if there is a duplicate or not
      if (data.indexOf(element, index + 1) > -1) {
        // Find if the element is already in the result array or not
        if (result.indexOf(element) === -1) {
          result.push(element);
        }
      }
    });
    return result;
  }

  async showSaveBallotSuccessMessage() {
    const alert = await this.alertController.create({
      cssClass: '',
      header: 'Complimenti!',
      message: `Il tuo ballottaggio è stato inserito correttamente. Lo trovi nella sezione "I miei ballottaggi"`,
      buttons: [{
        text: 'OK!',
        role: 'cancel',
        handler: async () => { }
      }]
    });
    return await alert.present();
  }

  async showEmptyPlayerErrorAlert() {
    const alert = await this.alertController.create({
      header: 'Attenzione!',
      message: 'Non hai inserito tutti i giocatori in questo ballottaggio! Inseriscili tutti e riprova!',
      buttons: [{
        text: 'OK!',
        role: 'cancel',
        handler: async () => { }
      }]
    });
    return await alert.present();
  }

  async showDuplicatePlayerErrorAlert() {
    const alert = await this.alertController.create({
      header: 'Attenzione!',
      message: 'Hai inserito uno o più giocatori uguali nello stesso ballottaggio!',
      buttons: [{
        text: 'OK!',
        role: 'cancel',
        handler: async () => { }
      }]
    });
    return await alert.present();
  }

}
